import {
    HttpErrorResponse,
    HttpHandler,
    HttpHeaderResponse,
    HttpInterceptor,
    HttpProgressEvent,
    HttpRequest,
    HttpResponse,
    HttpSentEvent,
    HttpUserEvent,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AuthenticationService } from '../authentication/authentication.service';
import { EnvironmentService } from '../environment.service';
import { getApplicationApi } from 'app/shared/utils/application-api';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private logoutScheduled = false;

    public constructor(private inj: Injector) {
    }

    public intercept(req: HttpRequest<any>, next: HttpHandler):
        Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
        return this.resolveAuthToken(req.url).pipe(
            switchMap((authToken) => {
                const request = this.createAuthorizationHeader(req, authToken);
                return next.handle(request).pipe(
                    catchError((err: HttpErrorResponse) => {
                        const api = getApplicationApi(request.url, this.inj.get(EnvironmentService).environment);
                        if (err.status === 401 && api && api !== 'auth' &&
                            request.headers.has('Authorization')) {
                            return this.handleError401(request, err);
                        }
                        return throwError(() => err);
                    }),
                );
            }),
        );
    }

    /**
     * Creates an authorization request header.
     */
    private createAuthorizationHeader(req: HttpRequest<any>, authToken: string): HttpRequest<any> {
        const containAPI = req.url.includes('/api/invoices?page=');
        const language = sessionStorage.getItem('lang') ? (sessionStorage.getItem('lang') === 'de') && (containAPI) ? 'en' : sessionStorage.getItem('lang') : 'en';
        const setHeaders: Record<string, string> = {
            'Accept-Language': language,
        };

        if (authToken && !req.headers.has('Authorization')) {
            setHeaders.Authorization = `Bearer ${ authToken }`;
        }

        // MV3-4690: keep explicit JSON content type for PUT when caller has not set it.
        if (req.method === 'PUT' && !req.headers.has('Content-Type')) {
            setHeaders['Content-Type'] = 'application/json; charset=utf-8';
        }

        return req.clone({ setHeaders });
    }

    private resolveAuthToken(url: string): Observable<string> {
        const auth = this.inj.get(AuthenticationService);
        // Access tokens come only from the Auth0 SDK and are sent only to the application's own APIs.
        if (!auth.isAuth0Enabled() ||
            !getApplicationApi(url, this.inj.get(EnvironmentService).environment)) {
            return of('');
        }

        return from(auth.getAuth0AccessToken()).pipe(
            map((token) => this.cleanToken(token)),
        );
    }

    private cleanToken(token: unknown): string {
        if (!token) {
            return '';
        }

        let cleanToken = token.toString().trim();
        if (cleanToken.startsWith('"') && cleanToken.endsWith('"')) {
            cleanToken = cleanToken.slice(1, -1);
        }

        return cleanToken;
    }

    private handleError401(req: HttpRequest<any>, error: HttpErrorResponse): Observable<any> {
        if (this.logoutScheduled) {
            return throwError(() => error);
        }

        this.logoutScheduled = true;
        const api = getApplicationApi(req.url, this.inj.get(EnvironmentService).environment);
        console.warn('[Session] Application API rejected authorization.', { api, method: req.method, status: 401 });
        setTimeout(() => {
            const authenticationService = this.inj.get(AuthenticationService);
            void authenticationService.logout(true, 'api-401', { api, method: req.method })
                .catch(() => console.error('[Session] Logout after API authorization failure failed.'))
                .finally(() => this.logoutScheduled = false);
        }, 1000);

        return throwError(() => error);
    }
}
